import { useState } from 'react'

function LandingPage() {
  const [showRequestForm, setShowRequestForm] = useState(false)
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    message: ''
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      // For now, simulate a successful submission since the backend endpoint doesn't exist yet
      // TODO: Replace this with actual API call when backend is implemented
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate network delay
      
      console.log('Access request submitted:', formData)
      setSuccess('Your access request has been submitted successfully. An administrator will review your request shortly.')
      setFormData({ fullName: '', email: '', company: '', message: '' })
      setShowRequestForm(false)
      
      // Uncomment this when backend is ready:
      /*
      const response = await fetch('/request-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status} ${response.statusText}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch {
          // If response is not JSON, use the status message
        }
        throw new Error(errorMessage)
      }

      setSuccess('Your access request has been submitted successfully. An administrator will review your request shortly.')
      setFormData({ fullName: '', email: '', company: '', message: '' })
      setShowRequestForm(false)
      */
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="landing-page">
      <div className="landing-container">
        {/* Header */}
        <header className="landing-header">
          <div className="landing-brand">
            <img 
              src={`/logo.png?v=${Date.now()}`} 
              alt="FarmCore" 
              className="landing-logo" 
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div className="brand-info">
              <h1 className="brand-title">FarmCore</h1>
              <p className="brand-subtitle">by Optiarc Systems AB</p>
            </div>
          </div>
          <div className="landing-actions">
            <button 
              className="btn btn-outline"
              onClick={() => setShowRequestForm(true)}
            >
              Request Access
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/login.html'}
            >
              Sign In
            </button>
          </div>
        </header>

        {/* Hero Section */}
        <section className="landing-hero">
          <div className="hero-content">
            <h2 className="hero-title">
              Modern Farm Management
              <span className="hero-accent">Simplified</span>
            </h2>
            <p className="hero-description">
              FarmCore is a comprehensive digital platform designed to streamline your agricultural operations. 
              Manage tasks, track livestock, monitor wildlife, and coordinate team activities all in one place.
            </p>
            <div className="hero-features">
              <div className="feature-item">
                <span className="feature-icon">📋</span>
                <span>Task Management</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📦</span>
                <span>Asset Tracking</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🦌</span>
                <span>Wildlife Monitoring</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🚁</span>
                <span>Drone Integration</span>
              </div>
            </div>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => window.location.href = '/login.html'}
            >
              Get Started Today
            </button>
          </div>
          <div className="hero-visual">
            <div className="hero-dashboard-preview">
              <div className="preview-header">
                <div className="preview-dots">
                  <span></span><span></span><span></span>
                </div>
                <span className="preview-title">FarmCore Dashboard</span>
              </div>
              <div className="preview-content">
                <div className="preview-stats">
                  <div className="preview-stat">
                    <span className="stat-icon">📋</span>
                    <span className="stat-value">12</span>
                    <span className="stat-label">Active Tasks</span>
                  </div>
                  <div className="preview-stat">
                    <span className="stat-icon">📦</span>
                    <span className="stat-value">847</span>
                    <span className="stat-label">Assets</span>
                  </div>
                  <div className="preview-stat">
                    <span className="stat-icon">🦌</span>
                    <span className="stat-value">23</span>
                    <span className="stat-label">Wildlife</span>
                  </div>
                </div>
                <div className="preview-cards">
                  <div className="preview-card">
                    <span className="card-icon">🚁</span>
                    <div className="card-content">
                      <strong>Drone Photos</strong>
                      <small>Latest aerial surveys</small>
                    </div>
                  </div>
                  <div className="preview-card">
                    <span className="card-icon">📊</span>
                    <div className="card-content">
                      <strong>Analytics</strong>
                      <small>Performance insights</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="landing-features">
          <h3 className="section-title">Why Choose FarmCore?</h3>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-card-icon">🎯</div>
              <h4>Task Coordination</h4>
              <p>Assign, track, and manage farm operations with real-time status updates and team collaboration.</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon">📍</div>
              <h4>Location-Based</h4>
              <p>Organize activities by sites and areas with GPS integration and interactive mapping.</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon">🔐</div>
              <h4>Secure & Private</h4>
              <p>Enterprise-grade security with role-based access control and data protection.</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon">📱</div>
              <h4>Mobile Ready</h4>
              <p>Access your farm data anywhere with responsive design optimized for all devices.</p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="landing-footer">
          <div className="footer-content">
            <div className="footer-brand">
              <strong>FarmCore</strong>
              <span>A product of Optiarc Systems AB</span>
            </div>
            <div className="footer-links">
              <button 
                className="footer-link"
                onClick={() => setShowRequestForm(true)}
              >
                Request Access
              </button>
              <button 
                className="footer-link"
                onClick={() => window.location.href = '/login.html'}
              >
                Sign In
              </button>
            </div>
          </div>
        </footer>
      </div>

      {/* Request Access Modal */}
      {showRequestForm && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Request Access to FarmCore</h3>
              <button 
                className="close-button"
                onClick={() => {
                  setShowRequestForm(false)
                  setError(null)
                  setSuccess(null)
                }}
              >
                ×
              </button>
            </div>

            {success && <div className="success">{success}</div>}
            {error && <div className="error">{error}</div>}

            <form onSubmit={handleSubmit} className="request-form">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="Enter your email address"
                />
              </div>

              <div className="form-group">
                <label>Company/Organization</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Enter your company name (optional)"
                />
              </div>

              <div className="form-group">
                <label>Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us about your farm and how you plan to use FarmCore..."
                  rows={4}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRequestForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default LandingPage
